import Image from "next/image";
import { Burger, Group, Menu, UnstyledButton } from "@mantine/core";
import { MdKeyboardArrowDown } from "react-icons/md";
import { NavbarProp } from "@components/hooks/utils/types/propTypes";
import PlanRecommendationSupport from "@components/atoms/PlanRecommendationSupport/PlanRecommendationSupport";
import Link from "next/link";

// Helper function to replace spaces with hyphens
const formatId = (text: string | any) =>
  text.replace(/\s+/g, "-").toLowerCase();

const ChevronDownIcon = ({id}) => {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 24 24"
      id="homepage-menu-navbar-plans-svg-plans"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path id={id} fill="none" d="M0 0h24v24H0V0z"></path>
      <path id={id} d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"></path>
    </svg>
  );
};

const MenuItem = ({ heading, links, categoryLink, mainHeading }) => (
  <div
    id={`homepage-menu-${formatId(mainHeading)}-${formatId(heading)}`}
    className="px-4 w-full sm:w-1/2 lg:w-1/4 pb-6 pt-6 lg:pt-3"
  >
    <a
      id={`homepage-category-${formatId(mainHeading)}-${formatId(heading)}`}
      className="font-bold text-md text-black text-bold mb-2"
      href={categoryLink}
    >
      {heading}
    </a>
    <ul id={`homepage-links-${formatId(mainHeading)}-${formatId(heading)}`}>
      {links.map(({ text, link }, index) => (
        <li
          key={index}
          id={`homepage-link-${formatId(mainHeading)}-${formatId(
            heading
          )}-${formatId(text)}`}
        >
          <a
            href={link}
            id={`homepage-link-text-${formatId(mainHeading)}-${formatId(
              heading
            )}-${formatId(text)}`}
            className="block p-3 hover:bg-amber-100 text-gray-700 text-xs rounded-md"
          >
            {text}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const MegaMenuContent = ({
  mainHeading,
  // mainDescription,
  picPath,
  iconPath,
  menusArray,
}) => (
  <div
    id={`homepage-mega-menu-${formatId(mainHeading)}`}
    role="toggle"
    className="p-6 mega-menu mb-16 w-[90vw] sm:mb-0 shadow-xl bg-white relative"
  >
    <div
      id={`homepage-menu-structure-${formatId(mainHeading)}`}
      className="mx-auto w-full flex justify-between"
    >
      {/* Customize or add more menu items as needed */}
      <div
        id={`homepage-icon-section-${formatId(mainHeading)}`}
        className="px-4 flex flex-col justify-center w-full sm:w-1/2 lg:w-1/4 pb-6 pt-6 lg:pt-3"
      >
        <Image
          src={iconPath}
          height={50}
          width={50}
          alt="icon"
          id={`homepage-icon-section-image-${formatId(mainHeading)}`}
        />

        <h3
          id={`homepage-heading-section-${formatId(mainHeading)}`}
          className="font-bold text-2xl text-black text-bold mb-2"
        >
          {mainHeading}
        </h3>
        {/* <p className="text-sm text-gray-700 mb-2">{mainDescription}</p> */}
      </div>
      {menusArray?.map((menu, index: number) => {
        return (
          <MenuItem
            key={index}
            heading={menu.category}
            categoryLink={menu.categoryLink}
            links={menu.subCategories}
            mainHeading={mainHeading}
          />
        );
      })}

      <ul
        id={`homepage-photo-section-${formatId(mainHeading)}`}
        className="px-4 w-full flex items-center sm:w-1/2 lg:w-1/4 pb-6 pt-6 lg:pt-3"
      >
        <Image
          id={`homepage-photo-image-${formatId(mainHeading)}`}
          src={picPath}
          height={200}
          width={200}
          alt="Photo"
        />
      </ul>

      {/* Add more menu items as needed */}
    </div>
  </div>
);

const NavbarDesktop = ({
  opened,
  toggle,
  headerList,
  headerPopover,
  PhoneJsx,
}: NavbarProp) => {
  return (
    <div className="mx-auto max-w-[1280px] w-full">
    <Group
      id="homepage-group-navbar"
      h="100%"
      wrap="nowrap"
      className="relative overflow-hidden ml-3"
    >
      <Burger
        id="homepage-burger-menu-navbar"
        opened={opened}
        onClick={toggle}
        hiddenFrom="sm"
        size="sm"
        color={opened ? "white" : "black"}
      />
      <Group
        id="homepage-group-navbar-child"
        justify="space-between"
        wrap="nowrap"
        style={{ flex: 1 }}
        gap="xs"
      >
        <Group
          id="homepage-logo-group-navbar"
          justify="space-between"
          wrap="nowrap"
          className="mx-auto md:mx-0"
        >
          <Link prefetch={false} href="/" id="homepage-main-logo-link-navbar">
            <Image
              id="homepage-main-logo-navbar"
              src="/home-page-images/logo-bar-images/Niva-Bupa-Logo.svg"
              width={85}
              height={57}
              alt="Best Health Insurance Company in India"
            />
          </Link>
          <Group
            id="header-links-navbar"
            ml="xs"
            gap={40}
            visibleFrom="sm"
            wrap="nowrap"
          >
            {(headerList ?? []).map(({ name, iconPath, chevronFlag, link }) => {
              return (
                <Menu
                  id={`homepage-menu-navbar-${formatId(name)}`}
                  trigger="hover"
                  openDelay={100}
                  // closeDelay={400}
                  key={name}
                  shadow="md"
                >
                  <Menu.Target>
                    <UnstyledButton
                      id={`homepage-menu-navbar-plans-${formatId(name)}`}
                      display="flex"
                      className="items-center gap-1"
                      onClick={() => {
                        if (link) {
                          window.open(link, "_blank");
                        }
                      }}
                    >
                      <span
                        className="text-sm"
                        id={`homepage-menu-navbar-plans-buttontext-${formatId(
                          name
                        )}`}
                      >
                        {name}
                      </span>{" "}
                      {chevronFlag && (
                        // <MdKeyboardArrowDown
                        //   id={`homepage-menu-navbar-plans-svg-${formatId(
                        //     name
                        //   )}`}
                        // />
                        <ChevronDownIcon id={`homepage-menu-navbar-plans-svg-${formatId(name)}`} />
                      )}
                    </UnstyledButton>
                  </Menu.Target>
                  {chevronFlag && (
                    <Menu.Dropdown>
                      <MegaMenuContent
                        mainHeading={name}
                        // mainDescription="When rendering several related Popovers, for example in a sites header navigation, use the Popover.Group com."
                        picPath="/ethinos-home-page/images/familyPhoto.jpg"
                        iconPath={iconPath}
                        menusArray={headerPopover[name]}
                      />
                    </Menu.Dropdown>
                  )}
                </Menu>
              );
            })}
          </Group>
        </Group>
        <PlanRecommendationSupport PhoneJsx={PhoneJsx} />
      </Group>
    </Group>
    </div>
  );
};

export default NavbarDesktop;
